import { submitClinicalNote, FlexiObservation } from '../api';
import { normalizeForSubmit } from '@/features/text-mode/utils/text-utils';
import { canRemoveEvidenceItem } from '../utils/evidence-removal';
import { useTrackStore } from '@/stores/track';
import type { useDraftStore } from '../stores/draft.store';
import type { useEvidenceStore } from '../stores/evidence.store';
import type { useNoteStore } from '../stores/note.store';
import type { usePatientStore } from '../stores/patient.store';
import type { useQuestionStore } from '../stores/question.store';
import type {
  AuditEntry,
  AssessmentScope,
  EvidenceCategory, MaternalPatientType, MaternalStatus,
  QuestionItem,
  TextModeExtraction,
  TextModeObservation,
  TextModePatientInfo,
  TextModeSubmitResponse,
} from '../types/text-mode.types';
import {useAlertStore} from "@/features/text-mode/stores/alert.store";

interface SubmitTextModeNoteParams {
  encounterId: string;
  rawText: string;
  engagement?: string;
  noteStore: ReturnType<typeof useNoteStore>;
  questionStore: ReturnType<typeof useQuestionStore>;
  evidenceStore: ReturnType<typeof useEvidenceStore>;
  draftStore: ReturnType<typeof useDraftStore>;
  patientStore: ReturnType<typeof usePatientStore>;
  alertStore: ReturnType<typeof useAlertStore>;
}

interface SubmitTextModeQnaAnswersParams {
  encounterId: string;
  engagement?: string;
  noteStore: ReturnType<typeof useNoteStore>;
  questionStore: ReturnType<typeof useQuestionStore>;
  evidenceStore: ReturnType<typeof useEvidenceStore>;
  draftStore: ReturnType<typeof useDraftStore>;
  patientStore: ReturnType<typeof usePatientStore>;
  alertStore: ReturnType<typeof useAlertStore>;
}

interface SubmitTextModeObservationEditsParams {
  encounterId: string;
  engagement?: string;
  noteStore: ReturnType<typeof useNoteStore>;
  questionStore: ReturnType<typeof useQuestionStore>;
  evidenceStore: ReturnType<typeof useEvidenceStore>;
  draftStore: ReturnType<typeof useDraftStore>;
  patientStore: ReturnType<typeof usePatientStore>;
  alertStore: ReturnType<typeof useAlertStore>;
}

function getPrefilledObservations(): FlexiObservation[] {
  const panelList = [
    'travel_to_malaria_zone',
    'travel_to_dengue_zone',
    'travel_to_chikungunya_zone',
    'travel_to_leptospirosis_zone',
    'travel_to_typhoid_zone',
    'travel_to_schistosomiasis_zone',
  ]

  return panelList.map((panelName) => ({
    name: panelName,
    value: 'yes',
    type: 'option',
    category: 'clinical_observation',
    source: 'computed',
  }));
}

export function convertQuestionsToObservations(questions: QuestionItem[]): FlexiObservation[] {
  const isUnknownAnswer = (answer: string): boolean => answer.trim().toLowerCase() === 'unknown';

  return questions
    .filter((q) => q.answer && String(q.answer).trim().length > 0)
    .reduce<FlexiObservation[]>((payload, q) => {
      let value: string | number | boolean | null = q.answer;

      if (q.type === 'number') {
        if (isUnknownAnswer(q.answer)) {
          value = 'Unknown';
        } else {
          const parsed = Number(q.answer);
          value = Number.isNaN(parsed) ? null : parsed;
        }
      }

      if (value === null) {
        return payload;
      }

      const observation: FlexiObservation = {
        name: q.id,
        value,
        type: q.type,
        category: q.observationCategory ?? 'patient',
        source: 'response',
      };
      if (q.input) observation.input = q.input;
      if (q.bloodPressureOptions) {
        observation.bloodPressureOptions = q.bloodPressureOptions;
      }
      payload.push(observation);

      return payload;
    }, []);
}

function applyTextModeResponse(
  response: TextModeSubmitResponse,
  noteStore: ReturnType<typeof useNoteStore>,
  questionStore: ReturnType<typeof useQuestionStore>,
  evidenceStore: ReturnType<typeof useEvidenceStore>,
  draftStore: ReturnType<typeof useDraftStore>,
  patientStore: ReturnType<typeof usePatientStore>,
  alertStore: ReturnType<typeof useAlertStore>,
): void {
  const trackStore = useTrackStore();
  if (
    (trackStore.initialAssessmentType === null || trackStore.initialAssessmentType === undefined)
    && response.focusedAssessments.length > 0
  ) {
    trackStore.startFocusedAssessment();
  }

  const isNewEscalation = response.isComprehensive
    && trackStore.initialAssessmentType === 'focused'
    && !trackStore.comprehensive;

  if (response.isComprehensive) {
    if (trackStore.initialAssessmentType === 'focused') {
      trackStore.escalateToComprehensiveAssessment({ keepFocusedSelections: true });
    } else {
      trackStore.startComprehensiveAssessment();
    }
  }
  trackStore.setFilterConditions(
    trackStore.isComprehensiveScope ? [] : response.conditions || [],
  );

  questionStore.resetQuestions();
  evidenceStore.reset();
  draftStore.fullReset();
  alertStore.reset();

  noteStore.setOriginalExtractions(response.extractions);
  noteStore.setOriginalObservations(response.observations);
  noteStore.setFocusedAssessments(response.focusedAssessments);
  patientStore.setPatient(response.patient);
  alertStore.buildAlertsFromResponse(response);
  evidenceStore.setEvidenceCategories(response.evidenceCategories);
  questionStore.setValidationContext(response.patient?.type ?? null);
  questionStore.setDangerSignHold(
    trackForPatientType(trackStore.all, response.patient?.type)?.flexiDangerSigns === true,
  );
  questionStore.setQuestions(response.questions);
  questionStore.setStage(response.questionStage);
  questionStore.setComprehensiveEscalationNotice(
    isNewEscalation && response.questions.length > 0,
  );
  draftStore.buildDraftFromTranscriptSubmit(response.evidenceCategories);
  noteStore.setSubmitted(true);
}

function lockedAssessmentScope(): AssessmentScope | undefined {
  return useTrackStore().isComprehensiveScope ? 'comprehensive' : undefined;
}

function observationKey(observation: Pick<TextModeObservation, 'category' | 'name'>): string {
  return `${observation.category}:${observation.name}`;
}

const UNKNOWN_VALUE = 'Unknown';

function isUnknownNumericValue(value: string | number | boolean | null): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    return trimmed === '' || trimmed === 'unknown';
  }
  return false;
}

function numericValue(value: string | number | boolean | null): number {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return parsed;
}

function coerceObservationValue(
  value: string | number | boolean | null,
  observation: TextModeObservation,
): string | number | boolean {
  if (observation.type === 'number') {
    if (isUnknownNumericValue(value)) return UNKNOWN_VALUE;
    return numericValue(value);
  }

  if (value === null || value === undefined) {
    return '';
  }

  return value;
}

function toMaternalStatus(value: unknown): MaternalStatus | null {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'antenatal' || normalized === 'postnatal' || normalized === 'none') {
    return normalized;
  }

  return null;
}

function isMaternalPatientType(value: MaternalStatus): value is MaternalPatientType {
  return value === 'antenatal' || value === 'postnatal';
}

export function buildObservationEditPayload(
  originalObservations: TextModeObservation[],
  categories: EvidenceCategory[],
  draftStore: ReturnType<typeof useDraftStore>,
): FlexiObservation[] {
  const editableItems = new Map(
    categories
      .flatMap(category => category.items)
      .filter(item => item.observationName && item.observationCategory)
      .map(item => [`${item.observationCategory}:${item.observationName}`, item]),
  );

  return originalObservations.reduce<FlexiObservation[]>((payload, observation) => {
    const item = editableItems.get(observationKey(observation));
    if (!item) {
      payload.push(observation as FlexiObservation);
      return payload;
    }

    const rawOverride = draftStore.draftOverrides[item.id];
    if (rawOverride?.markedIncorrect && canRemoveEvidenceItem(item, observation)) {
      return payload;
    }
    const override = rawOverride?.markedIncorrect ? undefined : rawOverride;

    const value = override && override.draftValue !== null && override.draftValue !== undefined
      ? override.draftValue
      : item.value;
    const source = override
      ? 'response'
      : (observation.source ?? item.source);

    const nextObservation: FlexiObservation = {
      ...observation,
      name: item.observationName ?? observation.name,
      type: item.observationType ?? observation.type,
      category: item.observationCategory ?? observation.category,
      source: source as FlexiObservation['source'],
      nameToken: item.nameToken ?? observation.nameToken,
      responseOptions: item.responseOptions ?? observation.responseOptions ?? null,
      numericOptions: item.numericOptions ?? observation.numericOptions ?? null,
      value: coerceObservationValue(value, observation),
    };
    const input = item.input ?? observation.input;
    const bloodPressureOptions = item.bloodPressureOptions
      ?? observation.bloodPressureOptions;
    if (input) nextObservation.input = input;
    if (bloodPressureOptions) nextObservation.bloodPressureOptions = bloodPressureOptions;
    payload.push(nextObservation);

    return payload;
  }, []);
}

export function buildExtractionEditPayload(
  originalExtractions: TextModeExtraction[],
  categories: EvidenceCategory[],
  draftStore: ReturnType<typeof useDraftStore>,
  originalObservations: TextModeObservation[] = [],
): TextModeExtraction[] {
  const removedExtractionIds = new Set<string>();
  const removedExactNames = new Set<string>();
  const observationsByExtractionId = new Map(
    originalObservations
      .filter(observation => observation.extractionID)
      .map(observation => [String(observation.extractionID), observation] as const),
  );
  const observationsByKey = new Map(
    originalObservations
      .map(observation => [observationKey(observation), observation] as const),
  );
  const observationsByName = new Map(
    originalObservations
      .map(observation => [observation.name.trim().toLowerCase(), observation] as const),
  );

  const originalObservationForItem = (
    item: EvidenceCategory['items'][number],
  ): TextModeObservation | undefined => {
    if (item.extractionID) {
      const byExtractionId = observationsByExtractionId.get(String(item.extractionID));
      if (byExtractionId) return byExtractionId;
    }

    if (item.observationName && item.observationCategory) {
      const byKey = observationsByKey.get(`${item.observationCategory}:${item.observationName}`);
      if (byKey) return byKey;
    }

    const exactName = item.observationName ?? item.name;
    return exactName ? observationsByName.get(exactName.trim().toLowerCase()) : undefined;
  };

  categories.forEach((category) => {
    category.items.forEach((item) => {
      if (!draftStore.draftOverrides[item.id]?.markedIncorrect) return;
      if (!canRemoveEvidenceItem(item, originalObservationForItem(item))) return;
      if (item.extractionID) {
        removedExtractionIds.add(String(item.extractionID));
        return;
      }
      const exactName = item.observationName ?? item.name;
      if (exactName) {
        removedExactNames.add(exactName.trim().toLowerCase());
      }
    });
  });

  if (removedExtractionIds.size === 0 && removedExactNames.size === 0) {
    return originalExtractions;
  }

  return originalExtractions.filter((extraction) => {
    if (extraction.extractionID && removedExtractionIds.has(String(extraction.extractionID))) {
      return false;
    }
    if (removedExactNames.has(extraction.name.trim().toLowerCase())) {
      return false;
    }
    return true;
  });
}

export async function submitTextModeNote(params: SubmitTextModeNoteParams): Promise<void> {
  const {
    encounterId,
    rawText,
    engagement,
    noteStore,
    questionStore,
    evidenceStore,
    draftStore,
    patientStore,
    alertStore
  } = params;

  const normalizedText = normalizeForSubmit(rawText);
  if (normalizedText.length === 0) return;

  noteStore.setText(normalizedText);
  noteStore.setSubmitting(true);
  noteStore.setSubmitted(false);

  const prefill = getPrefilledObservations();

  try {
    const response = await submitClinicalNote(encounterId, normalizedText, {
      engagement,
      processNote: true,
      observations: prefill,
    });

    applyTextModeResponse(response, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore);
  } finally {
    noteStore.setSubmitting(false);
  }
}

export async function submitTextModeQnaAnswers(params: SubmitTextModeQnaAnswersParams): Promise<void> {
  const { encounterId, engagement, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore } = params;

  if (!questionStore.prepareQuestionsForSubmit()) return;

  const note = noteStore.clinicalNote.text;
  const qnaObservations = convertQuestionsToObservations(questionStore.questions);
  const existingObservations = noteStore.originalObservations as FlexiObservation[];
  const combinedObservations = [...existingObservations, ...qnaObservations];
  let patient = patientStore.patient;
  const originalExtractions = noteStore.originalExtractions;

  noteStore.setSubmitting(true);
  noteStore.setSubmitted(false);

  /* Update patient type by maternal_status */
  const maternalStatusObservation = qnaObservations.find(obs => obs.name === 'maternal_status');
  const maternalStatus = toMaternalStatus(maternalStatusObservation?.value);
  if (maternalStatus && maternalStatus !== "none") {
    patient = {
      ...(patient ?? {}),
      maternal_status: maternalStatus,
    };
    if (isMaternalPatientType(maternalStatus)) patient.type = maternalStatus;
    patientStore.setPatient(patient);
    questionStore.setValidationContext(patient.type ?? null);
  }

  try {
    const response = await submitClinicalNote(encounterId, note, {
      engagement,
      processNote: false,
      observations: combinedObservations,
      patient,
      extractions: originalExtractions,
      assessmentScope: lockedAssessmentScope(),
    });

    applyTextModeResponse(response, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore);
  } finally {
    noteStore.setSubmitting(false);
  }
}

export async function submitTextModeComprehensiveContinuation(
  params: SubmitTextModeQnaAnswersParams,
): Promise<void> {
  const { encounterId, engagement, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore } = params;

  noteStore.setSubmitting(true);

  try {
    const response = await submitClinicalNote(encounterId, noteStore.clinicalNote.text, {
      engagement,
      processNote: false,
      observations: noteStore.originalObservations as FlexiObservation[],
      patient: patientStore.patient,
      extractions: noteStore.originalExtractions,
      assessmentScope: 'comprehensive',
    });

    useTrackStore().escalateToComprehensiveAssessment({ keepFocusedSelections: true });
    applyTextModeResponse(response, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore);
  } finally {
    noteStore.setSubmitting(false);
  }
}

export async function submitTextModeObservationEdits(
  params: SubmitTextModeObservationEditsParams,
): Promise<AuditEntry[]> {
  const { encounterId, engagement, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore } = params;

  if (draftStore.isRhsSubmitting || draftStore.hasValidationErrors) return [];
  if (!draftStore.prepareWarningsForSubmit()) return [];

  const note = noteStore.clinicalNote.text;
  const patient = patientStore.patient;
  const originalExtractions = noteStore.originalExtractions;
  const observations = buildObservationEditPayload(
    noteStore.originalObservations,
    evidenceStore.categories,
    draftStore,
  );
  const filteredExtractions = buildExtractionEditPayload(
    originalExtractions,
    evidenceStore.categories,
    draftStore,
    noteStore.originalObservations,
  );

  draftStore.setRhsSubmitting(true);

  try {
    const response = await submitClinicalNote(encounterId, note, {
      engagement,
      processNote: false,
      observations,
      patient,
      extractions: filteredExtractions,
      assessmentScope: lockedAssessmentScope(),
    });

    const auditEntries = draftStore.commitDraftToEvidenceChanges();
    applyTextModeResponse(response, noteStore, questionStore, evidenceStore, draftStore, patientStore, alertStore);
    return auditEntries;
  } finally {
    draftStore.setRhsSubmitting(false);
  }
}


interface TrackInfo {
  id: string;
  name: string;
  path: string;
  flexiDangerSigns?: boolean;
}

function trackForPatientType(tracks: unknown, patientType?: string | null): TrackInfo | undefined {
  if (!patientType) return undefined;
  const allTracks: TrackInfo[] = Array.isArray(tracks) ? tracks : [];
  return allTracks.find(track => track.path.toLowerCase() === patientType.toLowerCase());
}

export async function setTrackByPatientType(
  params: {
    tracks?: unknown;
    patientType: "newborn" | "child" | "youth" | "adult" | "antenatal" | "postnatal"
  },
): Promise<void> {
  const { tracks, patientType } = params;
  const matchingTrack = trackForPatientType(tracks, patientType);

  if (matchingTrack) {
    useTrackStore().selectTrack(matchingTrack);
  }
}

interface SetStreamlinedOptionsParameters {
  noteStore: ReturnType<typeof useNoteStore>;
  filterConditions?: string[];
}

interface StreamlinedOption {
  id: string;
  name: string;
  ordering: number;
  preselect: boolean;
  selected: boolean;
  value: string;
}

export async function setStreamlinedOptions(
  params: SetStreamlinedOptionsParameters,
): Promise<void> {
  const { noteStore, filterConditions } = params;

  const trackStore = useTrackStore();
  await trackStore.initializeStreamlinedOptions();

  const allFocusOptions : StreamlinedOption[] = trackStore.streamlinedOptions || [];
  const selectedOptions = allFocusOptions
    .filter(focusOption => noteStore.focusedAssessments.includes(focusOption.value))
    .map(focusOption => focusOption.value);

  await trackStore.setStreamlinedTrack({
    selectedStreamlinedOptions: selectedOptions,
    filterConditions,
  })
}
